// Client-side template-variable resolution for the preselected (kanban) path.
// Recipients are built in the browser from leads.getMany results, so the only
// lead attributes offered are the ones that query returns.
import { contactDisplay } from "@/lib/contact";
export type VariableMapping = Record<
  string,
  { source: "lead_attribute" | "static"; value: string; fallback?: string }
>;

export type MappableLead = {
  name?: string;
  // A phoneless lead is a real recipient now — it is reached by its WhatsApp id
  // — so all three handles are carried here, and the phone variable resolves
  // through them rather than to "".
  phone?: string | null;
  whatsappUsername?: string | null;
  whatsappUserId?: string | null;
};

// The same handle the merchant sees for this lead everywhere else in the
// portal: their number, else "@usuario", else the WhatsApp id.
const contactValue = (lead: MappableLead): string => {
  const display = contactDisplay(lead);
  return display.kind === "none" ? "" : display.value;
};

export const ATTRIBUTE_GROUPS = [
  {
    label: "Lead",
    options: [
      { value: "name", label: "Nombre" },
      { value: "phone", label: "Teléfono" },
    ],
  },
  {
    label: "Empresa",
    options: [{ value: "merchantName", label: "Nombre de la empresa" }],
  },
];

export const resolveVariableValue = (
  mapping: VariableMapping[string],
  lead: MappableLead,
  merchantName: string
): string => {
  if (mapping.source === "static") return mapping.value;
  const attrMap: Record<string, string> = {
    name: lead.name?.trim() || "",
    // Falls through to whatever handle the lead does carry — their @username,
    // else their WhatsApp id — rather than resolving to "". Meta rejects an
    // empty template body parameter, so a phoneless recipient mapped to this
    // attribute would otherwise fail the send with an opaque error. A lead that
    // has a phone still resolves to it unformatted, exactly as before.
    phone: lead.phone?.trim() || contactValue(lead),
    merchantName,
  };
  return attrMap[mapping.value] || mapping.fallback || "";
};

export const renderTemplatePreview = (
  bodyText: string,
  variableMapping: VariableMapping,
  lead: MappableLead,
  merchantName: string
): string => {
  let preview = bodyText;
  for (const [param, mapping] of Object.entries(variableMapping)) {
    const value = resolveVariableValue(mapping, lead, merchantName);
    preview = preview.replace(`{{${param}}}`, value || `[${param}]`);
  }
  return preview;
};
